import React from 'react';
import { showLoading, hideLoading } from 'react-redux-loading-bar';
import { connect } from 'react-redux';
import UrlPattern from 'url-pattern';
import classNames from 'classnames';
import pt from 'prop-types';
import trimStart from 'lodash/trimStart';
import trimEnd from 'lodash/trimEnd';
import find from 'lodash/find';

import api from 'services/api';
import actions from 'actions';

import Button from 'components/button';
import Loading from 'components/loading';
import ViewLoading from 'components/view/loading';
import StorefrontEditorHeader from 'components/storefront/editor/header';
import { HeaderStatus } from 'components/storefront/editor/header/status';

import CheckoutSettingsMenu from 'editor/src/components/SettingsMenu/CheckoutSettingsMenu';

import {
  loadSettings,
  resetFiles,
  publishCheckout,
  fetchPublishStatus,
  updateEditorSection,
  sendEditorMessage,
  handleEditorMessage,
  updatePublishStatus,
} from 'editor/src/actions';

import { formatDate } from 'utils';

import {
  storefrontCheckoutURL,
  storefrontPublicDeploymentURL,
} from 'utils/storefront';

import 'editor/src/libs/croppie/croppie.css';

let USING_MESSAGE_API;
let EDITOR_URL_FULL = '';

// Isolate style imports to prevent overriding from "EditorLayoutV1.js"
function loadStyles() {
  import('./CheckoutEditorLayout.scss');
  import('editor/src/components/SettingsMenu/CheckoutSettingsMenu.scss');
}

window.editorRefreshPage = (callback = null) => {
  if (USING_MESSAGE_API) {
    if (callback) {
      callback();
    }
    return;
  }

  /** @type {HTMLIFrameElement | null} */
  const iframe = document.getElementById('editor-viewport');

  if (iframe) {
    // forces a reload while avoiding cors and content policy issues
    iframe.src = iframe.src;
  }

  function onLoad(event) {
    callback(event);
    iframe.removeEventListener('load', onLoad);
  }

  if (callback) {
    if (iframe) {
      iframe.addEventListener('load', onLoad);
    } else {
      callback();
    }
  }
};

@connect(
  (state) => ({
    client: state.client,
    account: state.account,
    loading: state.loading,
    session: state.session,
    storefront: state.storefronts.storefront,
    storefronts: state.storefronts,
    hasPreview: !!state.storefronts.storefront?.preview_deployment_name,
    testCheckout: state.storefronts.testCheckout,
    checkoutSettings: state.settings.checkout,
    showEditor: state.editor.showEditor,
    editorSection: state.editor.section,
    editorLoading: state.editor.loading,
    themeConfig: state.themeConfig,
    publishStatus: state.git.publishStatus,
    flash: state.flash,
  }),
  (dispatch) => ({
    loadSettings: () => dispatch(loadSettings()),
    resetFiles: () => dispatch(resetFiles()),
    publishCheckout: () => dispatch(publishCheckout()),
    fetchPublishStatus: (storefrontId) =>
      dispatch(fetchPublishStatus(storefrontId)),
    updatePublishStatus: (data) => dispatch(updatePublishStatus(data)),
    fetchAllStorefronts: () => dispatch(actions.storefronts.fetchAll()),
    fetchStorefront: (id) => dispatch(actions.storefronts.fetch(id)),
    publishStorefront: (id, $publish = true) =>
      dispatch(actions.storefronts.update(id, { $publish })),
    getTestCheckout: (reset) =>
      dispatch(actions.storefronts.getTestCheckout(reset)),
    showLoading: () => dispatch(showLoading()),
    hideLoading: () => dispatch(hideLoading()),
    updateSection: (section, scrollTop) =>
      dispatch(updateEditorSection(section, scrollTop)),
    notifyError: (message) => {
      dispatch(actions.flash.error(message));
    },
  }),
)
class CheckoutEditorLayout extends React.Component {
  static propTypes = {
    router: pt.object,
    params: pt.object,
    location: pt.object,
    loading: pt.bool,
    hasPreview: pt.bool,
    account: pt.object,
    client: pt.object,
    storefront: pt.object,
    testCheckout: pt.object,
    renderedLogs: pt.object,
    publishStatus: pt.object,
    editorSection: pt.object,
    themeConfig: pt.object,
    checkoutSettings: pt.object,
    showReloadNotice: pt.bool,

    showLoading: pt.func,
    hideLoading: pt.func,
    loadSettings: pt.func,
    updateSection: pt.func,
    fetchAllStorefronts: pt.func,
    fetchStorefront: pt.func,
    getTestCheckout: pt.func,
    publishCheckout: pt.func,
    publishStorefront: pt.func,
    fetchPublishStatus: pt.func,
    updatePublishStatus: pt.func,
    notifyError: pt.func,
  };

  static contextTypes = {
    openModal: pt.func.isRequired,
    testEnv: pt.string,
    user: pt.object,
  };

  constructor(props) {
    super(props);

    loadStyles();

    this.state = {
      loaded: false,
      viewport: 'fit-screen',
      show: true,
      editorLoaded: false,
      editorUrl: null,
      editorNavIndex: 0,
      editorNavStack: [],
      editorRoute: null,
      editorLocale: null,
      containerUrl: null,
      showPublishingModal: false,
      showLocaleSelector: false,
      appLogs: null,
      appLogSource: null,
      appLogLines: 100,
      showAppLogs: false,
      loadingAppLogs: false,
      showPublishLogs: false,
      publishProgress: 0,
      justPublished: false,
      usingMessageApi: USING_MESSAGE_API,
      inCheckout: false,
    };

    this.editorFrame = null;
    this.editorBound = false;
    this.editorConnected = false;
    this.navigatedBack = false;
    this.navigatedForward = false;
    this.loadingTimer = null;
  }

  async componentDidMount() {
    const {
      router,
      params,
      loadSettings,
      fetchAllStorefronts,
      fetchStorefront,
      fetchPublishStatus,
      notifyError,
    } = this.props;

    this.appLogsRef = React.createRef();
    this.publishLogsRef = React.createRef();

    const storefront = await fetchStorefront(params.id);

    // Storefront has to exist
    if (!storefront) {
      router.push('/storefronts');
      return;
    }

    await fetchAllStorefronts();
    await loadSettings();

    if (storefront.hosted) {
      if (!storefront.preview_deployment_name) {
        // setTimeout is used so the flash message doesn't get flushed before appearing.
        setTimeout(() => {
          notifyError(
            'A preview was not found for this storefront. Please contact our support team.',
          );
        }, 50);

        router.push('/storefronts');
        return;
      }

      fetchPublishStatus(storefront.id);
    }

    const section = this.setSectionFromQuery();
    const baseUrl = this.getBaseUrl();

    this.setState({
      storefront,
      loaded: true,
      containerUrl: section?.checkout ? undefined : baseUrl, // prevents flip/flop to checkout url
      usingMessageApi: (USING_MESSAGE_API = undefined),
      ...this.getFromState(this.props, storefront),
    });

    this.showLoading();

    this.editorConnected = false;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      this.props.editorSection !== nextProps.editorSection ||
      this.props.themeConfig !== nextProps.themeConfig
    ) {
      this.setStateFromSection(nextProps, this.props);
    }
  }

  componentDidUpdate() {
    this.editorFrame = document.getElementById('editor-viewport');
    if (this.editorFrame && !this.editorBound) {
      this.editorBound = true;
      this.editorFrame.addEventListener('load', this.onChangeEditorPage);
      handleEditorMessage('route.changed', this.handleRouteChange);
      handleEditorMessage('locale.changed', this.handleLocaleChange);
    }
  }

  componentWillUnmount() {
    if (this.editorBound) {
      this.editorFrame.removeEventListener('load', this.onChangeEditorPage);
      handleEditorMessage('route.changed', this.handleRouteChange, false);
    }
  }

  showLoading() {
    this.props.showLoading();

    // Auto hide loader after a few seconds
    this.loadingTimer = setTimeout(() => {
      if (this.props.loading) {
        this.hideLoading();
      }
    }, 5000);
  }

  hideLoading() {
    this.props.hideLoading();
    clearTimeout(this.loadingTimer);
  }

  setSectionFromQuery() {
    const { location, updateSection, editorSection } = this.props;

    const settings = location.query.section || location.query.setting;
    const pages = location.query.page;

    let section;

    if (settings === 'checkout') {
      section = { checkout: true };
    } else if (settings) {
      section = { settings };
    } else if (pages) {
      section = { pages };
    }

    if (section) {
      updateSection(section, 0);
      return section;
    } else if (editorSection?.checkout) {
      // Unset checkout section if not set in URL
      updateSection({ settings: null }, 0);
    }
  }

  getFromState(props, storefront) {
    const { location } = props;

    let fromUri;
    let fromLabel;

    // Special case
    if (location.query.section === 'checkout') {
      fromUri = '/storefronts/checkout';
      fromLabel = 'checkout settings';
    } else {
      fromUri = `/storefronts/${storefront.id}`;
      fromLabel = storefront.name;
    }

    return { fromUri, fromLabel };
  }

  getEditorUrl = (url = this.editorFrame.src) => {
    const baseUrl = this.getBaseUrl();
    return url.replace(baseUrl, '');
  };

  getEditorUrlFull = () => {
    const { containerUrl, editorUrl } = this.state;

    if (this.state.inCheckout && EDITOR_URL_FULL) {
      return EDITOR_URL_FULL;
    }

    if (editorUrl?.startsWith('http')) {
      EDITOR_URL_FULL = editorUrl;
    } else {
      EDITOR_URL_FULL = trimEnd(containerUrl, '/') + (editorUrl || '');
    }

    return EDITOR_URL_FULL;
  };

  onChangeEditorPage = () => {
    if (!this.state.editorLoaded) {
      this.setSectionFromQuery();
      const editorRoute = { location: this.getEditorUrl() };
      if (editorRoute?.location) {
        this.setState((state) => ({
          editorNavIndex: state.editorNavIndex + 1,
          editorNavStack: [...state.editorNavStack, editorRoute],
          editorRoute,
        }));
      }
    }

    // Detect when the frame navigates away from checkout in cases where the storefront is not a theme
    // This only happens after the editor is fully loaded
    if (this.state.inCheckout && !this.editorFrame.src.includes('/checkout/')) {
      this.setState({ inCheckout: false }, () => {
        this.setState({ editorUrl: this.getEditorUrl() });
      });
    } else {
      this.setState({ editorLoaded: true, editorUrl: this.getEditorUrl() });
      this.props.hideLoading();
    }
  };

  selectContent = (path) => {
    if (this.editorFrame) {
      sendEditorMessage('content.selected', { path });
    }
  };

  showLocaleSelector = (value) => {
    if (value !== this.state.showLocaleSelector) {
      this.setState({ showLocaleSelector: value });
    }
  };

  handleRouteChange = ({ details } = {}) => {
    if (!details) return;

    const { location, app } = details;

    if (!location) return;

    console.log('editor route', location);

    let { editorNavIndex, editorNavStack } = this.state;

    if (location && !this.state.editorLoaded) {
      this.setSectionFromQuery();
      this.setStateFromSection(this.props);
    }

    if (app === 'checkout') {
      this.setState({
        inCheckout: true,
        editorUrl: this.getEditorUrl(location),
      });
    } else {
      this.setState({
        inCheckout: false,
        editorUrl: this.getEditorUrl(location),
      });
    }

    this.setState({ editorLoaded: true });

    if (this.navigatedBack) {
      this.navigatedBack = false;
      this.hideLoading();
    } else if (this.navigatedForward) {
      this.navigatedForward = false;
      this.hideLoading();
    } else {
      const prevRoute = editorNavStack[editorNavStack.length - 1];

      if (!prevRoute || prevRoute.location !== location) {
        editorNavStack = editorNavStack.slice(0, editorNavIndex);
        this.setState({
          editorNavIndex: editorNavIndex + 1,
          editorNavStack: [...editorNavStack, details],
          editorRoute: details,
          usingMessageApi: (USING_MESSAGE_API = details.events !== false),
        });
      }
    }
  };

  handleLocaleChange = ({ details } = {}) => {
    if (!details) {
      return;
    }
    const { locale } = details;

    if (locale) {
      console.log('editor locale', locale);
      this.setState({ editorLocale: locale });
    }
  };

  onSelectViewport = (viewport) => this.setState({ viewport });

  getBaseUrl() {
    const { location, storefront, client } = this.props;

    if (this.state.inCheckout) {
      return storefrontCheckoutURL(client.id, true);
    }

    if (location.query.url) {
      return location.query.url;
    }

    if (storefront.external?.custom && storefront.external?.custom_url) {
      return storefront.external.custom_url;
    }

    return storefrontPublicDeploymentURL(storefront);
  }

  async setStateFromSection(props) {
    const { themeConfig, editorSection, getTestCheckout } = props;

    let relUrl = '';

    if (editorSection.settings && themeConfig.settings) {
      const setting = find(themeConfig.settings, {
        id: editorSection.settings,
      });
      if (setting && setting.url) {
        const pattern = new UrlPattern(setting.url);
        if (setting.id === 'checkout') {
          const { id } = await getTestCheckout();
          relUrl = pattern.stringify({ id: id || 'error' });
        } else {
          relUrl = pattern.stringify({ id: 'editor' });
        }
      }
    } else if (editorSection.checkout && themeConfig.checkout) {
      if (themeConfig.checkout.url) {
        const pattern = new UrlPattern(themeConfig.checkout.url);
        let { id } = await getTestCheckout();

        id = id || 'error';

        // Make sure that the environment ID is passed for swell checkout
        if (!this.props.checkoutSettings.custom_checkout) {
          const { testEnv } = this.context;
          id = testEnv ? `${testEnv}/${id}` : id;
        }

        relUrl = pattern.stringify({ id });
      }
    }

    if (relUrl) {
      // Reset checkout flag
      if (editorSection.checkout && !this.state.inCheckout) {
        // Await in order for getBaseUrl to get the flag
        await new Promise((resolve) =>
          this.setState({ inCheckout: true }, resolve),
        );
      }

      const baseUrl = this.getBaseUrl();
      EDITOR_URL_FULL = `${baseUrl}/${trimStart(relUrl, '/')}`;
      this.setFrameUrl(EDITOR_URL_FULL);
    }
  }

  setFrameUrl = (url = '/') => {
    const iframe = document.getElementById('editor-viewport');
    if (iframe) {
      const full = url.startsWith('http');
      const frameUrl = full
        ? url
        : `${this.getBaseUrl()}/${trimStart(url, '/')}`;

      if (iframe.src !== frameUrl) {
        iframe.src = frameUrl;
      }
    }
  };

  onClickResetTestCheckout = () => {
    this.props.getTestCheckout(true).then(() => {
      window.editorRefreshPage(() => this.setStateFromSection(this.props));
    });
  };

  onClickPublish = () => {
    const { publishStatus, publishCheckout } = this.props;

    if (!publishStatus.checkoutCurrent && !publishStatus.checkoutPublishing) {
      publishCheckout();
    }
  };

  onChangeNotifyPublishing = (event, value) => {
    api.put('/user', {
      notify_publish: !!value,
    });
  };

  renderStatus() {
    const { publishStatus } = this.props;

    return (
      <>
        {publishStatus.checkoutCurrent === false ? (
          <>
            {!publishStatus.publishing ? (
              <HeaderStatus type="warning">
                {publishStatus.date_checkout_published ? (
                  <>
                    Last published{' '}
                    {formatDate(publishStatus.date_checkout_published, 'ago')}
                  </>
                ) : (
                  <>Saved</>
                )}
              </HeaderStatus>
            ) : (
              <HeaderStatus loading>Publishing checkout</HeaderStatus>
            )}
          </>
        ) : (
          <HeaderStatus>Published</HeaderStatus>
        )}
      </>
    );
  }

  render() {
    const { testCheckout, publishStatus } = this.props;

    const {
      loaded,
      containerUrl,
      viewport,
      showLocaleSelector,
      editorUrl,
      editorRoute,
      editorLocale,
      editorLoaded,
      usingMessageApi,
    } = this.state;

    if (!loaded) {
      return <ViewLoading />;
    }

    return (
      <div className="CheckoutEditorLayout storefront-editor">
        <StorefrontEditorHeader
          {...this.props}
          viewport={viewport}
          showLocaleSelector={showLocaleSelector}
          status={this.renderStatus()}
          published={publishStatus.checkoutCurrent}
          publishing={publishStatus.publishing}
          previewUrl={this.getEditorUrlFull()}
          onChangeViewport={this.onSelectViewport}
          onPublish={this.onClickPublish}
        />
        <div className="CheckoutEditorLayout-container">
          <div
            className={classNames('CheckoutEditorLayout-side', {
              hidden: viewport === 'expand',
            })}
          >
            <div className="CheckoutEditorLayout-side-menu">
              <CheckoutSettingsMenu
                key="settings"
                {...this.props}
                editorUrl={editorUrl}
                editorRoute={editorRoute}
                editorLoaded={editorLoaded}
                editorLocale={editorLocale}
                usingMessageApi={usingMessageApi}
                containerUrl={containerUrl}
                setFrameUrl={this.setFrameUrl}
                selectContent={this.selectContent}
                showContentPublishedWarning={this.showContentPublishedWarning}
                showLocaleSelector={this.showLocaleSelector}
              />
            </div>
          </div>
          <div className="CheckoutEditorLayout-content">
            <div className="CheckoutEditorLayout-view">
              <div id="modal-viewport" />
              <div id="tooltip-viewport" />
              <div className="CheckoutEditorLayout-view-container">
                <div className="CheckoutEditorLayout-view-body">
                  <div
                    className={classNames(
                      'CheckoutEditorLayout-view-frame',
                      viewport,
                    )}
                  >
                    <div className="CheckoutEditorLayout-view-chrome">
                      {!editorLoaded && (
                        <div className="CheckoutEditorLayout-view-chrome-loading">
                          <Loading />
                        </div>
                      )}
                      <iframe
                        id="editor-viewport"
                        src={containerUrl}
                        title="Editor"
                      />
                      {testCheckout && testCheckout.order_id && (
                        <div className="CheckoutEditorLayout-view-notice">
                          Your test checkout is complete. Reset it to start
                          over.
                          <Button
                            size="sm"
                            onClick={this.onClickResetTestCheckout}
                            type="default"
                          >
                            Reset checkout
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default CheckoutEditorLayout;
